import { useState, useEffect } from "react";
import Card from "../components/Card";
import { RowCarousel } from "../components/RowCarousel";
import PageHero from "../components/PageHero";
import { SkeletonRow } from "../components/SkeletonCard";
import { posterFallback } from "../constants/media";

type Recommendation = {
  name: string;
  imagePath: string | null;
  pathToDir: string;
  type: string;
  score: number;
  reason: string;
};

export default function ForYou() {
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDir, setSelectedDir] = useState("");

  useEffect(() => {
    fetch("/api/recommendations?limit=5", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((data: Recommendation[]) => setRecs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => {
      fetch("/api/recommendations?limit=5", { credentials: "same-origin" })
        .then((r) => r.json())
        .then((data: Recommendation[]) => setRecs(data))
        .catch(() => {});
    };
    window.addEventListener("ossflix-media-updated", handler);
    return () => window.removeEventListener("ossflix-media-updated", handler);
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <PageHero title="For You" subtitle="Recommendations based on your watch history." />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  // Group recommendations by their top genre (extracted from reason)
  const grouped = new Map<string, Recommendation[]>();
  for (const rec of recs) {
    // Extract the first genre from "Because you watch Action, Drama"
    const match = rec.reason.match(/Because you watch (.+)/);
    const genres = match ? match[1].split(", ") : ["Recommended"];
    const topGenre = genres[0] || "Recommended";
    const key = `Because you watch ${topGenre}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(rec);
  }

  // Check if title was added within 7 days (using current date as reference)
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  return (
    <div style={{ minHeight: "100vh" }}>
      <PageHero
        title="For You"
        subtitle="Recommendations based on your watch history."
        images={recs.map((r) => r.imagePath ?? "")}
      />

      {recs.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "4rem 2rem",
            margin: "0 4%",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "12px",
            border: "1px solid var(--oss-border)",
          }}
        >
          <svg
            aria-hidden="true"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--oss-text-muted)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginBottom: "16px", opacity: 0.5 }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <p style={{ color: "var(--oss-text-muted)", fontSize: "0.95rem", margin: 0 }}>
            No recommendations yet. Start watching something and we'll suggest similar titles!
          </p>
        </div>
      )}

      {[...grouped.entries()].map(([groupLabel, groupRecs]) => (
        <section key={groupLabel} className="oss-section">
          <h2 className="oss-section-title">{groupLabel}</h2>
          <RowCarousel role="list">
            {groupRecs.map((rec) => (
              <div
                key={rec.pathToDir}
                className="oss-card"
                role="button"
                aria-label={rec.name}
                onClick={() => setSelectedDir(rec.pathToDir)}
              >
                {rec.imagePath ? (
                  <img
                    src={rec.imagePath}
                    alt={rec.name}
                    className="oss-card-img"
                    loading="lazy"
                    decoding="async"
                    onError={posterFallback}
                  />
                ) : (
                  <div
                    className="oss-card-img"
                    style={{
                      background: "var(--oss-bg-elevated)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--oss-text-muted)",
                      fontSize: "2rem",
                    }}
                  >
                    ?
                  </div>
                )}
                <div className="oss-card-title-bar">
                  <span>{rec.name}</span>
                </div>
              </div>
            ))}
          </RowCarousel>
        </section>
      ))}

      <Card show={!!selectedDir} onHide={() => setSelectedDir("")} dirPath={selectedDir} />
    </div>
  );
}
